import type React from 'react';
import { Icon } from '@iconify/react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip';
import { cn } from '@/lib/utils';
import type { DayBlock } from '@/components/admin/booking/bookingCalendarBlocks';
import { formatTimeRange } from '@/components/admin/booking/bookingCalendarUtils';

const HATCH = 'bg-[repeating-linear-gradient(135deg,#f8fafc_0,#f8fafc_7px,#f1f5f9_7px,#f1f5f9_8px)]';

interface BookingSlotFaceProps {
  block: DayBlock;
  detailed?: boolean;
  fieldLabel?: string;
  isOngoing?: boolean;
  isPast?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick: () => void;
}

function getSlotPaymentTheme(paymentStatus?: string, isOngoing?: boolean, isPast?: boolean) {
  if (isOngoing) {
    return {
      card: 'bg-[#FC480D] border-[#FC480D] text-white shadow-xs',
      name: 'text-white',
      time: 'text-white/90',
    };
  }
  if (isPast) {
    return {
      card: 'bg-[#F4F6F8] border-[#E0E6ED] text-gray-500 hover:bg-gray-100',
      name: 'text-gray-700',
      time: 'text-gray-400',
    };
  }
  const status = (paymentStatus || '').toLowerCase();
  if (status === 'paid') {
    return {
      card: 'bg-[#ECFDF3] border-[#0BCF46]',
      name: 'text-[#027A48]',
      time: 'text-[#027A48]/85',
    };
  }
  if (status === 'dp_paid' || status === 'dp' || status === 'partial') {
    return {
      card: 'bg-[#FFF5EE] border-[#FC480D]',
      name: 'text-[#FC480D]',
      time: 'text-[#FC480D]/85',
    };
  }
  // Default: Belum Bayar (unpaid, pending, dll)
  return {
    card: 'bg-[#FEF2F2] border-[#E7515A]',
    name: 'text-[#B91C1C]',
    time: 'text-[#B91C1C]/85',
  };
}

export default function BookingSlotFace({
  block,
  detailed,
  isOngoing,
  isPast,
  className,
  style,
  onClick,
}: BookingSlotFaceProps) {
  if (block.kind === 'booked') {
    const slot = block.slots[0];
    const theme = getSlotPaymentTheme(slot?.payment_status, isOngoing, isPast);

    return (
      <button
        type="button"
        onClick={onClick}
        style={style}
        className={cn(
          'absolute inset-x-2 z-10 overflow-hidden rounded-xl border px-3 py-2.5 text-left transition-all hover:opacity-95 shadow-xs',
          theme.card,
          className
        )}
      >
        <span className={cn('block truncate text-xs font-semibold leading-tight', theme.name)}>
          {slot?.customer_name || 'Booking'}
        </span>
        <span className={cn('mt-1 block truncate text-[11px] font-medium leading-none', theme.time)}>
          {detailed ? formatTimeRange(block.start, block.end) : block.start}
        </span>
      </button>
    );
  }

  if (block.kind === 'selected') {
    return (
      <button
        type="button"
        onClick={onClick}
        style={style}
        className="absolute inset-1 rounded-lg border-2 border-[#FC480D] bg-[#FFF5EE] transition-colors"
        aria-label="Sesi dipilih"
      />
    );
  }

  if (block.kind === 'blocked' || block.kind === 'locked') {
    const isBlocked = block.kind === 'blocked';
    const button = (
      <button
        type="button"
        onClick={onClick}
        style={style}
        className={cn('absolute inset-1 rounded-lg flex items-center justify-center transition-colors cursor-not-allowed', HATCH)}
        aria-label={isBlocked ? `Sesi ${block.start} ditutup (Custom)` : 'Lapangan tutup'}
      >
        <Icon icon="ph:lock-simple" className="h-4 w-4 text-gray-400" />
      </button>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{isBlocked ? `Sesi ${block.start} ditutup (Custom)` : 'Lapangan tutup'}</TooltipContent>
      </Tooltip>
    );
  }

  if (isPast) {
    return (
      <button
        type="button"
        disabled
        style={style}
        className="absolute inset-1 rounded-lg bg-gray-50/70 border border-gray-100 flex items-center justify-center cursor-not-allowed select-none"
        aria-label={`Sesi ${block.start} telah terlewat`}
      >
        <span className="text-[10px] text-gray-300 font-medium">Terlewat</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className="absolute inset-1 rounded-lg bg-transparent transition-colors hover:bg-orange-50/40 cursor-pointer"
      aria-label={`Booking ${block.start}`}
    />
  );
}
